import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { trackUserEvent } from '@vend/utilities'
import { Dispatch, useEffect, useRef } from 'react'
import { WEB_REGISTER } from '../../../../../../web-register.constant'
import { SwiperError, TerminalInfo } from '../../../../helpers/lspay.types'
import {
  LSPAY_REFUND_TRANSACTION_STATUS,
  TRANSACTION_TYPE,
} from '../../../../helpers/lspayConstants'
import { getPollingTimeout } from '../../../../helpers/lspayTransaction'
import { getRefund, getRefundError } from '../../../../helpers/refund'
import { TAG, trackLSPayServerError } from '../../../../hooks/errorTracking'
import { sendTerminalEvent } from '../../../../helpers/terminalMonitoringEventDispatcher'
import { isRetryableError } from '../../../../helpers/errorChecking'
import { RefundEvent } from './use-lspay-refund-server-reducer/types'
import { useDispatchRefundEvent } from './useDispatchRefundEvent'

interface PollRefundProps {
  dispatch: Dispatch<RefundEvent>
  isCardPresented?: boolean
  registerId: string
  registerSalePaymentId: string
  setupIntentId: string
  terminalMonitoringInstance: TerminalEventMonitoring | null
  terminalInfo: TerminalInfo
}
export const usePollRefund = ({
  dispatch,
  isCardPresented,
  registerId,
  registerSalePaymentId,
  setupIntentId,
  terminalMonitoringInstance,
  terminalInfo,
}: PollRefundProps) => {
  const active = useRef(true)
  const { dispatchEvent, getErrorMessage } = useDispatchRefundEvent()

  useEffect(() => {
    // Reset the active status polling to true when there is change in isCardPresented value
    // Since on card presented we need to restart the polling
    active.current = true
  }, [isCardPresented])
  useEffect(() => {
    // Track user event for polling
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSD started polling refund status',
      {
        registerId,
        registerSalePaymentId,
        setupIntentId,
      }
    )

    const pollTransaction = () => {
      // if it is not active anymore, don't poll
      if (!active.current) {
        return
      }

      getRefund({
        registerId,
        setupIntentId,
        registerSalePaymentId,
      })
        .then(data => {
          // if it is not active anymore, don't continue polling
          if (!active.current) {
            return
          }
          if (
            data.status === LSPAY_REFUND_TRANSACTION_STATUS.PROCESSING ||
            (data.status === LSPAY_REFUND_TRANSACTION_STATUS.CARD_PRESENTED &&
              isCardPresented)
          ) {
            // Continue polling in the following cases:
            // - The refund is in the "processing" state.
            // - The refund is in the "card presented" state, and isCardPresented is true.
            //   (Polling was restarted the first time the "card presented" status was encountered,
            //   so we need to keep polling for subsequent occurrences of this status.)
            timeoutId = setTimeout(pollTransaction, getPollingTimeout())
            return
          }
          dispatchEvent({
            dispatch,
            setupIntentId,
            terminalInfo,
            data,
            terminalMonitoringInstance,
          })
        })
        .catch((error: SwiperError) => {
          trackLSPayServerError(
            '[lspay-server-driven] failed to get refund',
            error,
            {
              registerId,
              registerSalePaymentId,
              setupIntentId,
            },
            TAG.refund
          )

          // In cases of some specific errors, which is related to the unrefernce settings
          // we cann't continue polling, becasue result aren't going to change.
          // so we need to show an error message to the user
          if (!isRetryableError(error.code)) {
            // Send terminal monitoring event for error
            sendTerminalEvent({
              terminalMonitoringInstance,
              terminal: {
                id: terminalInfo?.deviceId,
                model: terminalInfo?.deviceModel,
              },
              reason:
                error.message ||
                `Syncing refund status failed due to unreferenced settings`,
              swiperError: error,
              transactionType: TRANSACTION_TYPE.REFUND,
            })
            dispatch({
              type: 'ERROR',
              errorMessage: getErrorMessage(getRefundError(error.code)),
              disableRetry: true,
            })
            return
          }

          // keep polling
          timeoutId = setTimeout(pollTransaction, getPollingTimeout())
          // TODO would we ever want to stop polling here ? maybe if the response indicates a bad request
        })
    }

    let timeoutId = setTimeout(pollTransaction, getPollingTimeout())

    return () => {
      clearTimeout(timeoutId)
    }
  }, [
    terminalInfo,
    dispatch,
    isCardPresented,
    registerId,
    registerSalePaymentId,
    setupIntentId,
    terminalInfo.deviceId,
    terminalInfo.deviceModel,
    terminalMonitoringInstance,
    dispatchEvent,
    getErrorMessage,
  ])
  // return a function to cancel polling
  return () => {
    active.current = false
  }
}
